import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData, GstExpenseProps } from '../data/types';
import { ScreenSection, ScreenDivider } from '../primitives/ScreenPrimitives';
import { coverFonts, ptToPx, space } from '../data/designTokens';
import {
    DASH,
    chartCard,
    chartColors,
    ScreenStatStrip,
    ScreenChartCard,
    ScreenLegend,
    ScreenLineChart,
    ScreenGrowthBars,
    ScreenHBars,
    ScreenStackedBars,
    ScreenDualAreaChart,
    ScreenMonthTable,
} from '../primitives/GstChartPrimitives';

const SF = coverFonts.screen;

const getData = (data: ReportData): GstExpenseProps | undefined => data.gstExpenses;

// ════════════════════════════════════════════════════════════════
//                            S C R E E N
// ════════════════════════════════════════════════════════════════
const ScreenChartsPage2: React.FC<{ d: GstExpenseProps }> = ({ d }) => {
    const m = d.chartMeta ?? {};
    return (
        <div style={{ marginTop: ptToPx(space.sp8) }}>
            <ScreenDivider />
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap), marginTop: ptToPx(space.sp6) }}>
                <ScreenChartCard title="Expenses" value={m.expenses?.primaryText ?? DASH} sub={m.expenses?.secondaryText ?? ''}>
                    {d.expenseTrend ? <ScreenLineChart data={d.expenseTrend} color={chartColors.neg} /> : null}
                    <ScreenLegend items={[{ label: 'Expenses (₹ Lacs)', color: chartColors.neg }]} />
                </ScreenChartCard>
                <ScreenChartCard
                    title="Expense Growth"
                    value={m.expenseGrowth?.primaryText ?? DASH}
                    sub={m.expenseGrowth?.secondaryText ?? ''}
                >
                    {d.expenseGrowth ? <ScreenGrowthBars data={d.expenseGrowth} /> : null}
                    <ScreenLegend
                        items={[
                            { label: 'Positive', color: chartColors.pos },
                            { label: 'Negative', color: chartColors.neg },
                        ]}
                    />
                </ScreenChartCard>
            </div>
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap), marginTop: ptToPx(chartCard.gridGap) }}>
                {d.vendorConcentration ? (
                    <ScreenChartCard
                        title="Vendor Expense Concentration"
                        value={m.concentration?.primaryText ?? `${d.vendorConcentration.majorPct}%`}
                        valueSmall
                        sub={m.concentration?.secondaryText ?? 'of expenses from top vendors'}
                    >
                        {/* Split bar + legend — only the real percentage split from
                        the graph; counts/amounts are not sent by this API. */}
                        <div
                            style={{
                                marginTop: ptToPx(8),
                                display: 'flex',
                                height: ptToPx(6),
                                borderRadius: ptToPx(3),
                                overflow: 'hidden',
                            }}
                        >
                            <div style={{ flex: d.vendorConcentration.majorPct || 1, background: chartColors.neg }} />
                            <div style={{ flex: d.vendorConcentration.minorPct || 1, background: chartColors.negSoft }} />
                        </div>
                        <div style={{ marginTop: ptToPx(8), display: 'flex', gap: ptToPx(12) }}>
                            <div style={{ flex: 1, borderLeft: `${ptToPx(2.25)}px solid ${chartColors.neg}`, paddingLeft: ptToPx(6) }}>
                                <div style={{ fontSize: ptToPx(6.75), fontFamily: SF.sans, color: '#555555' }}>
                                    {d.vendorConcentration.majorLabel || 'Top vendors'}
                                </div>
                                <div style={{ fontSize: ptToPx(8.25), fontFamily: SF.mono, fontWeight: 500, color: '#333333' }}>
                                    {d.vendorConcentration.majorPct}% of expenses
                                </div>
                            </div>
                            <div
                                style={{
                                    flex: 1,
                                    borderLeft: `${ptToPx(2.25)}px solid ${chartColors.negSoft}`,
                                    paddingLeft: ptToPx(6),
                                }}
                            >
                                <div style={{ fontSize: ptToPx(6.75), fontFamily: SF.sans, color: '#555555' }}>
                                    {d.vendorConcentration.minorLabel || 'Other vendors'}
                                </div>
                                <div style={{ fontSize: ptToPx(8.25), fontFamily: SF.mono, fontWeight: 500, color: '#333333' }}>
                                    {d.vendorConcentration.minorPct}% of expenses
                                </div>
                            </div>
                        </div>
                    </ScreenChartCard>
                ) : (
                    <div style={{ flex: 1 }} />
                )}
                <ScreenChartCard
                    title="Top 5 Expense Sources"
                    value={m.topVendors?.primaryText ?? DASH}
                    valueSmall
                    sub={m.topVendors?.secondaryText ?? ''}
                >
                    {d.topVendors ? <ScreenHBars items={d.topVendors} barColor={chartColors.neg} /> : null}
                </ScreenChartCard>
            </div>
        </div>
    );
};
const ScreenChartsPage3: React.FC<{ d: GstExpenseProps }> = ({ d }) => {
    const m = d.chartMeta ?? {};
    return (
        <div style={{ marginTop: ptToPx(space.sp6) }}>
            <div style={{ display: 'flex', gap: ptToPx(chartCard.gridGap) }}>
                {d.existingVsNewVendors ? (
                    <ScreenChartCard
                        title="Existing Vs New Vendors"
                        value={m.existingVsNew?.primaryText ?? DASH}
                        valueSmall
                        sub={m.existingVsNew?.secondaryText ?? ''}
                    >
                        <ScreenDualAreaChart
                            existing={d.existingVsNewVendors.existing}
                            newData={d.existingVsNewVendors.new}
                            primaryColor={chartColors.neg}
                            secondaryColor={chartColors.negSoft}
                            height={140}
                        />
                        <ScreenLegend
                            items={[
                                { label: 'Existing', color: chartColors.neg },
                                { label: 'New', color: chartColors.negSoft },
                            ]}
                        />
                    </ScreenChartCard>
                ) : (
                    <div style={{ flex: 1 }} />
                )}
                <ScreenChartCard
                    title="Split of Recurring Vendors"
                    value={m.recurring?.primaryText ?? DASH}
                    valueSmall
                    sub={m.recurring?.secondaryText ?? ''}
                >
                    {d.recurringVendors ? (
                        <ScreenStackedBars data={d.recurringVendors} secondaryFill={chartColors.negSoft} height={140} />
                    ) : null}
                    <ScreenLegend
                        items={[
                            { label: 'Recurring', color: chartColors.pos },
                            { label: 'Non Recurring', color: chartColors.negSoft },
                        ]}
                    />
                </ScreenChartCard>
            </div>
        </div>
    );
};

const ScreenGstExpenses: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const d = getData(data);
    if (!d) return null;
    return (
        <ScreenSection
            kicker="Expenses"
            title="GST Expenses"
            subtitle={`Expense analysis from GSTR-2A/2B & ITC claims · Source: GST Portal${d.gstin ? ` · GSTIN: ${d.gstin}` : ''}`}
            index={index}
        >
            <ScreenStatStrip stats={d.stats} />
            {/* Charts first, tables below — per design feedback */}
            {d.expenseTrend || d.expenseGrowth ? <ScreenChartsPage2 d={d} /> : null}
            {d.existingVsNewVendors || d.recurringVendors ? <ScreenChartsPage3 d={d} /> : null}
            {/* Headers must come from the GSTR-3B response (gstr2MonthHeaders) —
                the B2B vendor monthHeaders use different labels and every cell
                would miss. */}
            {d.gstr2Rows.length > 0 ? (
                <div style={{ marginTop: ptToPx(space.sp12) }}>
                    <ScreenDivider />
                    <div style={{ marginTop: ptToPx(space.sp8) }}>
                        <ScreenMonthTable
                            variant="classification"
                            title="Purchases & ITC Summary (GSTR-3B)"
                            firstColLabel="Classifi."
                            headers={d.gstr2MonthHeaders ?? []}
                            rows={d.gstr2Rows.map((r) => ({
                                label: r.classification,
                                category: r.category,
                                bold: r.bold,
                                amounts: r.amounts,
                            }))}
                            footer="All amounts in ₹"
                        />
                    </div>
                </div>
            ) : null}
            {d.b2bVendors.length > 0 ? (
                <div style={{ marginTop: ptToPx(space.sp12) }}>
                    <ScreenDivider />
                    <div style={{ marginTop: ptToPx(space.sp8) }}>
                        <ScreenMonthTable
                            variant="b2b"
                            title="B2B Vendor Summary"
                            subtitle="Top vendors by invoice value from GSTR-2A/2B · Data from connected GSTs"
                            firstColLabel="Vendor"
                            headers={d.monthHeaders}
                            rows={d.b2bVendors.map((r) => ({ label: r.name, bold: r.bold, isTotal: r.isTotal, amounts: r.amounts }))}
                            footer="All amounts in ₹ · Showing top 20 vendors"
                        />
                    </div>
                </div>
            ) : null}
        </ScreenSection>
    );
};

export const gstExpensesModule: ModuleDefinition = {
    meta: {
        id: 'gstExpenses',
        title: 'GST Expenses',
        description: 'GSTR-2A/2B expense analysis, ITC utilisation, vendor concentration and B2B summary.',
        dataSource: 'GSTR-2A/2B, GST Portal',
    },
    hasData: (d) => !!d.gstExpenses && (d.gstExpenses.gstr2Rows.length > 0 || d.gstExpenses.b2bVendors.length > 0),
    Screen: ScreenGstExpenses,
};
